import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Plus, Edit, Trash2, X, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import ImageUploader from '../../components/ImageUploader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBrands = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [saving, setSaving] = useState(false);

  const languages = ['sk', 'en', 'de', 'cs', 'ru'];
  const languageNames = { sk: 'Slovensky', en: 'English', de: 'Deutsch', cs: 'Česky', ru: 'Русский' };

  const emptyForm = {
    name: '',
    logo_url: '',
    website: '',
    translations: {
      sk: { name: '', description: '' },
      en: { name: '', description: '' },
      de: { name: '', description: '' },
      cs: { name: '', description: '' },
      ru: { name: '', description: '' }
    }
  };

  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const response = await fetch(`${API_URL}/api/brands`);
      if (response.ok) {
        const data = await response.json();
        setBrands(data);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({
      name: brand.name || '',
      logo_url: brand.logo_url || '',
      website: brand.website || '',
      translations: brand.translations || emptyForm.translations
    });
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingBrand(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const url = editingBrand 
        ? `${API_URL}/api/admin/brands/${editingBrand.id}`
        : `${API_URL}/api/admin/brands`;
      
      const method = editingBrand ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchBrands();
        setShowForm(false);
        setEditingBrand(null);
      }
    } catch (error) {
      console.error('Error saving brand:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (brandId) => {
    if (!window.confirm(t('admin.confirm_delete'))) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/brands/${brandId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchBrands();
      }
    } catch (error) {
      console.error('Error deleting brand:', error);
    }
  };

  const updateTranslation = (lang, field, value) => {
    setFormData(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...prev.translations[lang],
          [field]: value
        }
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{t('nav.brands')}</h1>
        <Button onClick={handleCreate} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="h-4 w-4 mr-2" />
          {t('admin.add_item')}
        </Button>
      </div>

      {/* Brands Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {brands.map(brand => (
          <div key={brand.id} className="bg-white rounded-xl shadow p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold">{brand.name}</h3>
                {brand.website && (
                  <a href={brand.website} target="_blank" rel="noopener noreferrer" 
                     className="text-sm text-orange-500 hover:underline">
                    {brand.website}
                  </a>
                )}
              </div>
              {brand.logo_url && (
                <img src={brand.logo_url} alt={brand.name} className="h-12 w-12 object-contain" />
              )}
            </div>
            <p className="text-gray-600 text-sm mb-4 line-clamp-2">
              {brand.translations?.sk?.description || brand.translations?.en?.description || '-'}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handleEdit(brand)}>
                <Edit className="h-4 w-4 mr-1" /> {t('admin.edit')}
              </Button>
              <Button variant="outline" size="sm" className="text-red-500" onClick={() => handleDelete(brand.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">
                {editingBrand ? t('admin.edit') : t('admin.add_item')} - {t('nav.brands')}
              </h2>
              <button onClick={() => setShowForm(false)}>
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{t('admin.name')} *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Armolan, Madico..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Website</label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                    placeholder="https://..."
                  />
                </div>
              </div>

              {/* Logo */}
              <div>
                <label className="block text-sm font-medium mb-1">Logo</label>
                <ImageUploader
                  value={formData.logo_url}
                  onChange={(url) => setFormData(prev => ({ ...prev, logo_url: url }))}
                />
              </div>

              {/* Translations */}
              <div>
                <h3 className="font-semibold mb-3">{t('admin.translations')}</h3>
                <div className="space-y-4">
                  {languages.map(lang => (
                    <div key={lang} className="border rounded-lg p-4">
                      <h4 className="font-medium mb-2">{languageNames[lang]}</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">{t('admin.name')}</label>
                          <Input
                            value={formData.translations[lang]?.name || ''}
                            onChange={(e) => updateTranslation(lang, 'name', e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">{t('admin.description')}</label>
                          <textarea
                            className="w-full border rounded-lg p-2 text-sm"
                            rows={2}
                            value={formData.translations[lang]?.description || ''}
                            onChange={(e) => updateTranslation(lang, 'description', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowForm(false)}>
                {t('admin.cancel')}
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
                {saving ? t('admin.saving') : <><Save className="h-4 w-4 mr-2" />{t('admin.save')}</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBrands;
